/**
 * ImageSection component for the dish form
 */

import React from 'react';
import PictureUploader from '../../pages/pictureUploader.js';
import { getTranslation } from '../../utils/translate.js';
import { DISH_STATUS } from '../../utils/dishFormUtils.js';

/**
 * Spacing constants for consistent layout
 * Using 8px increments (0.5rem = 8px in Tailwind)
 */
const SPACING = {
  // Typography and section styles
  SECTION_TITLE: 'text-xl font-bold mb-4 border-b pb-2 text-gray-800 dark:text-[#c9d1d9] border-gray-200 dark:border-[#30363d]',

  // Layout styles
  GRID: 'grid grid-cols-1 md:grid-cols-2 gap-6 mt-4',
  CONTAINER: 'bg-white dark:bg-[#0d1117] p-4 rounded-lg border dark:border-[#30363d]',
  FLEX: 'flex flex-col md:flex-row gap-6 items-start',

  // Tips styles
  TIPS_CONTAINER: 'bg-white dark:bg-[#161b22] p-3 rounded-lg border border-gray-200 dark:border-[#30363d]',
  TIPS_TITLE: 'text-sm text-gray-600 dark:text-[#8b949e] mb-2',
  TIPS_LIST: 'text-sm text-gray-600 dark:text-[#8b949e] list-disc pl-5 space-y-1',
};

/**
 * ImageSection component
 * @param {Object} props - Component props
 * @returns {JSX.Element} ImageSection component
 */
const ImageSection = ({
  formData,
  image,
  setImage,
  errors,
  language
}) => {
  return (
    <div>
      <h2 className={SPACING.SECTION_TITLE}>
        {getTranslation('menu.image', language)}
      </h2>
      <div className={SPACING.GRID}>
        <div className="md:col-span-2">
          <div className={SPACING.CONTAINER}>
            <div className={SPACING.FLEX}>
              <div className="md:w-1/2">
                <PictureUploader 
                  dishId={formData.id} 
                  dishImage={formData.url}
                  setImage={(file) => setImage(file)} 
                  image={image}
                  onImageRemoved={() => {
                    setImage(null);
                    // Clear the URL in formData when image is removed
                    // This is handled in the parent component via the setImage callback
                  }} 
                  validationError={errors?.url}
                  isEditable={formData.status !== DISH_STATUS.PENDING}
                />
              </div>
              <div className="md:w-1/2">
                <div className={SPACING.TIPS_CONTAINER}>
                  <p className={SPACING.TIPS_TITLE}>
                    <span className="font-semibold">
                      {getTranslation('menu.imageTips', language)}:
                    </span>
                  </p>
                  <ul className={SPACING.TIPS_LIST}>
                    <li>{getTranslation('menu.imageHighQuality', language)}</li>
                    <li>{getTranslation('menu.imageGoodLighting', language)}</li>
                    <li>{getTranslation('menu.imageCentered', language)}</li>
                    <li>{getTranslation('menu.imageAvoidDark', language)}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageSection;
